import React from "react";
import { motion } from "framer-motion";
import { fmtMoney, fmtInvestment } from "@/lib/api";
import {
  Activity,
  Users,
  Wallet,
  TrendingDown,
  Gauge,
  ShieldCheck,
  Clock,
} from "lucide-react";

const Card = ({ icon: Icon, label, value, sub, accent, testId }) => (
  <div
    className={`rounded-xl border p-5 flex flex-col justify-between min-h-[132px] ${
      accent ? "border-[#1E6BFF]/25" : "border-white/10"
    }`}
    style={{ backgroundColor: accent ? "#101a33" : "#0F1424" }}
    data-testid={testId}
  >
    <div
      className={`flex items-center gap-2 text-sm ${accent ? "text-[#66B2FF]" : "text-white/55"}`}
    >
      <Icon size={16} /> {label}
    </div>
    <div className="mt-3">
      <div
        className={`font-bold tabular-nums leading-tight ${accent ? "text-[#66B2FF]" : "text-white"} text-2xl`}
      >
        {value}
      </div>
      {sub ? <div className="text-white/45 text-xs mt-1.5">{sub}</div> : null}
    </div>
  </div>
);

export const ExecutiveDashboard = ({ result, showPricing = false }) => {
  const d = result?.executive_dashboard;
  if (!d) return null;
  const reservePct = Math.round((d.reserve_target_pct || 0) * 100);
  const team = d.recommended_team || [];

  return (
    <motion.section
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      data-testid="executive-dashboard"
    >
      <h2 className="text-2xl font-bold mb-1">Executive summary at a glance</h2>
      <p className="text-white/60 mb-6">
        The headline numbers &mdash; with the full methodology below.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 items-stretch">
        <Card
          icon={Activity}
          label="Estimated workload"
          value={`${d.estimated_workload_hours} hrs/month`}
          sub="After assessment adjustments"
          testId="exec-workload"
        />
        <Card
          icon={Users}
          label="Recommended capacity"
          value={`${d.recommended_seats} full-time ${d.recommended_seats === 1 ? "seat" : "seats"}`}
          sub={`${d.capacity_provided_hours} hrs/month`}
          testId="exec-capacity"
        />
        {showPricing && (
          <Card
            icon={Wallet}
            label="Estimated Monthly Investment"
            value={fmtInvestment(d.monthly_investment_low, d.monthly_investment_high)}
            testId="exec-investment"
          />
        )}
        {showPricing && (
          <Card
            icon={TrendingDown}
            label="Estimated Monthly Savings"
            value={`${fmtMoney(d.monthly_savings)}/month`}
            testId="exec-savings"
          />
        )}
        <Card
          icon={Gauge}
          label="Expected utilization"
          value={`${d.utilization_percent}%`}
          sub={`Includes ${reservePct}% reserve target`}
          testId="exec-utilization"
        />
        <Card
          icon={ShieldCheck}
          label="AI confidence"
          value={`${d.confidence_score}%`}
          sub={`${d.confidence_label}`}
          accent
          testId="exec-confidence"
        />
      </div>

      {/* Implementation as a small secondary line */}
      <div className="flex items-center gap-2 text-white/55 text-sm mt-4" data-testid="exec-implementation">
        <Clock size={15} className="text-white/45" />
        Estimated implementation: <span className="text-white/80 font-medium">{d.implementation_estimate}</span>
      </div>

      {/* Separate full-width Recommended Team section */}
      <div
        className="rounded-xl border border-white/10 p-5 sm:p-6 mt-6"
        style={{ backgroundColor: "#0F1424" }}
        data-testid="recommended-team"
      >
        <div className="flex items-center gap-2 text-white/55 text-sm mb-3">
          <Users size={16} /> Recommended team
        </div>
        <div className="flex flex-wrap gap-x-8 gap-y-2">
          {team.map((r, i) => (
            <div key={i} className="text-white font-semibold text-lg">
              {r.quantity} {r.name}
            </div>
          ))}
        </div>
        <div className="text-white/45 text-sm mt-3">
          {d.recommended_seats} total {d.recommended_seats === 1 ? "seat" : "seats"}
        </div>
      </div>
    </motion.section>
  );
};

export default ExecutiveDashboard;
